import { computed } from "vue";
import { PoundsPerCubicFoot } from "@/libs/units/density";
import { getTotalNameplateVoltageForFrequencyV } from "@/libs/calculations/motors";
import { getPumpEfficiencyRatio } from "@/libs/calculations/pumps";
import {
  getSurfaceDriveRequiredKVA,
  getSurfaceDriveSystemMonthlyCost,
  getMotorStartupVoltage,
  getTransformerTapData
} from "@/libs/calculations/surfaceDrive";
import { KiloWatts, Watts } from "@/libs/units/power";
import { DEFAULT_INPUT_LINE_VOLTAGE_V } from "@/libs/calculations/constants";
import type { SurfaceDriveCtx } from "./context";

// Surface drive (VSD/transformer) and system efficiency calcs.
export const defineSurfaceDrive = (ctx: SurfaceDriveCtx) => {
  const {
    _equipment,
    _parameters,
    dischargeFluidDensityLBMFT3,
    requiredPumpBrakeHP,
    producedTotalDynamicHeadFt,
    dischargeFlowRateSTBD,
    motorData,
    motorInputW,
    reratedNameplateV,
    reratedNameplateA,
    motorVoltageV,
    motorVoltage60HzV,
    motorEfficiencyRatio,
    sealOperatingHP,
    totalImpedance,
    totalCablesVoltLossV,
    totalCablesVoltLossAtNameplateAtMaxHzV,
    totalCablesPowerLossKW,
    totalCablesVoltLossV60Hz
  } = ctx;

  const totalSystemEfficiencyRatio = computed(() => {
    return (
      cableEfficiencyRatio.value *
      sealEfficiencyRatio.value *
      pumpEfficiencyRatio.value *
      motorEfficiencyRatio.value
    );
  });

  const cableEfficiencyRatio = computed(() => {
    const effRatio = 1 - totalCablesPowerLossKW.value / systemPowerKW.value;
    return isFinite(effRatio) ? effRatio : 0;
  });

  const sealEfficiencyRatio = computed(() => {
    const effRatio =
      1 - KiloWatts.toThisUnit(sealOperatingHP.value) / systemPowerKW.value;
    return isFinite(effRatio) ? effRatio : 0;
  });

  const pumpEfficiencyRatio = computed(() => {
    return getPumpEfficiencyRatio(
      dischargeFlowRateSTBD.value,
      producedTotalDynamicHeadFt.value,
      PoundsPerCubicFoot.toStandardUnit(dischargeFluidDensityLBMFT3.value),
      requiredPumpBrakeHP.value
    );
  });

  // Grabs the user’s or system’s max frequency for the surface drive
  const surfaceDriveMaxHz = computed(
    () => _parameters.value?.maximum_frequency || 60
  );

  const surfaceDriveMinHz = computed(
    () => _parameters.value?.minimum_frequency || 40
  );

  // calcs coming from VSD_Setup__Ben_s_Voltage_drop_Program_
  const motorVoltageAtMaxHzV = computed(() => {
    return getTotalNameplateVoltageForFrequencyV(
      reratedNameplateV.value,
      surfaceDriveMaxHz.value
    );
  });

  // I6 VSD_Setup__Ben_s_Voltage_drop_Program_
  const requiredVoltageAtMaxHzV = computed(() => {
    return (
      motorVoltageAtMaxHzV.value + totalCablesVoltLossAtNameplateAtMaxHzV.value
    );
  });

  // i 12 vOLTS @ 60hz - VSD_Setup__Ben_s_Voltage_drop_Program_
  const requiredVoltageAtMaxHz60HzV = computed(() => {
    return requiredVoltageAtMaxHzV.value * (60 / surfaceDriveMaxHz.value);
  });

  // i22 - VSD_Setup__Ben_s_Voltage_drop_Program_
  const kvaAtMaxHz = computed(() => {
    return getSurfaceDriveRequiredKVA(
      requiredVoltageAtMaxHzV.value,
      reratedNameplateA.value
    );
  });

  // i16 I Limit - VSD_Setup__Ben_s_Voltage_drop_Program_
  const iLimitA = computed(
    () => 1.05 * reratedNameplateA.value * transformerRatio.value
  );

  const iLimitSync = computed(
    () => reratedNameplateA.value * 1.5 * transformerRatio.value
  );

  // i18 underload - VSD_Setup__Ben_s_Voltage_drop_Program_
  const vsdUnderloadA = computed(
    () => 0.7 * reratedNameplateA.value * transformerRatio.value
  );

  // i20 overload
  const vsdOverloadA = computed(
    () => 1.2 * reratedNameplateA.value * transformerRatio.value
  );

  // i14 VSD Volts @ 60Hz - VSD_Setup__Ben_s_Voltage_drop_Program_
  const maxVSDOutputVoltage60HzV = computed(() => {
    return requiredVoltageAtMaxHz60HzV.value / transformerRatio.value;
  });

  const transformerTapData = computed(() =>
    getTransformerTapData(
      _equipment.value?.r_step_up_transformer?.min_secondary_voltage || 0,
      _equipment.value?.r_step_up_transformer?.max_secondary_voltage || 0,
      requiredVoltageAtMaxHzV.value
    )
  );

  // VSD Operational Calculations =====================================================
  const requiredVoltageV = computed(
    () => motorVoltageV.value + totalCablesVoltLossV.value
  );

  const requiredVoltage60HzV = computed(
    () => motorVoltage60HzV.value + totalCablesVoltLossV60Hz.value
  );

  const transformerRatio = computed(
    () =>
      transformerTapData.value.availableVoltage /
      _parameters.value?.vsd_input_voltage
  );

  const vsdOutputVoltageV = computed(
    () => requiredVoltageV.value / transformerRatio.value
  );

  const requiredKVA = computed(
    () =>
      getSurfaceDriveRequiredKVA(
        requiredVoltageV.value,
        motorData.value.motor_current
      ) || 0
  );

  const stepDownTransformerLoadRatio = computed(
    () =>
      requiredKVA.value /
      ((_parameters.value?.vsd_input_voltage / DEFAULT_INPUT_LINE_VOLTAGE_V) *
        (_equipment.value?.r_step_down_transformer?.kva || 1))
  );

  const stepUpTransformerLoadRatio = computed(
    () =>
      requiredKVA.value /
      ((_parameters.value?.vsd_input_voltage / DEFAULT_INPUT_LINE_VOLTAGE_V) *
        (_equipment.value?.r_step_up_transformer?.kva || 1))
  );

  const vsdKVARating = computed(
    () =>
      (_parameters.value?.vsd_input_voltage / DEFAULT_INPUT_LINE_VOLTAGE_V) *
      (_equipment.value?.r_vsd?.kva || 1)
  );

  const vsdLoadRatio = computed(() => requiredKVA.value / vsdKVARating.value);

  // CO2 per metric ton
  const systemEmissionsCO2T = computed(() => requiredKVA.value * 0.0004246);

  const requiredKVA60Hz = computed(
    () =>
      getSurfaceDriveRequiredKVA(
        requiredVoltage60HzV.value,
        reratedNameplateA.value
      ) || 0
  );

  const maxAmpRatingA = computed(() => {
    return transformerRatio.value * reratedNameplateA.value;
  });

  const systemPowerKW = computed(() => {
    const hp = Watts.toStandardUnit(motorInputW.value);
    return KiloWatts.toThisUnit(hp) + totalCablesPowerLossKW.value;
  });

  const surfacePFRatio = computed(() => {
    return systemPowerKW.value / requiredKVA.value;
  });

  const monthlySystemCostUSD = computed(() =>
    getSurfaceDriveSystemMonthlyCost(
      systemPowerKW.value,
      _parameters.value?.cable_cost_kw_hr_usd
    )
  );

  const startupVoltage = computed(() =>
    getMotorStartupVoltage(
      totalImpedance.value,
      reratedNameplateV.value,
      reratedNameplateA.value
    )
  );

  const startUpVoltageNamplateRatio = computed(() => {
    return startupVoltage.value / reratedNameplateV.value;
  });

  const vfdLoadRatio = computed(() => {
    if (!_equipment.value?.r_vsd?.kva || _equipment.value?.r_vsd?.kva === 0) {
      return 0;
    }
    return requiredKVA.value / vsdKVARating.value;
  });

  return {
    totalSystemEfficiencyRatio,
    cableEfficiencyRatio,
    sealEfficiencyRatio,
    pumpEfficiencyRatio,
    surfaceDriveMaxHz,
    surfaceDriveMinHz,
    motorVoltageAtMaxHzV,
    requiredVoltageAtMaxHzV,
    requiredVoltageAtMaxHz60HzV,
    kvaAtMaxHz,
    iLimitA,
    iLimitSync,
    vsdUnderloadA,
    vsdOverloadA,
    maxVSDOutputVoltage60HzV,
    transformerTapData,
    requiredVoltageV,
    requiredVoltage60HzV,
    transformerRatio,
    vsdOutputVoltageV,
    requiredKVA,
    stepDownTransformerLoadRatio,
    stepUpTransformerLoadRatio,
    vsdKVARating,
    vsdLoadRatio,
    systemEmissionsCO2T,
    requiredKVA60Hz,
    maxAmpRatingA,
    systemPowerKW,
    surfacePFRatio,
    monthlySystemCostUSD,
    startupVoltage,
    startUpVoltageNamplateRatio,
    vfdLoadRatio
  };
};

export type SurfaceDriveCalcs = ReturnType<typeof defineSurfaceDrive>;
